import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import FruitifyIcon from "../FruitifyIcons";
import { HelpOutline } from "@mui/icons-material";
import { SortableQuestionList } from "../../features/question/components/SortableQuestionList";
import { QuestionExtraction } from "../../features/question/components/QuestionExtraction";
import { CreateQuestionDialog } from "../../features/question/components/CreateQuestionDialog";
import { JobsAccordion } from "../JobsAccordion";
import { CreateQuizDialog } from "../../features/quiz/components/CreateQuizDialog";
import { ChooseExistingQuestionDialog } from "./ChooseExistingQuestionDialog";

const AddQuestionActionCard = ({
  title,
  subtitle,
  iconName,
  onAdd,
  disabled = false,
  showAIChip = false,
}) => (
  <Card
    variant="outlined"
    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
  >
    <FruitifyIcon name={iconName} variant="background" />
    <CardContent sx={{ mt: 2, mb: 2, flexGrow: 1 }}>
      <Stack direction={"row"} spacing={2} alignItems={"center"}>
        <Typography variant="h5">{title}</Typography>
        {showAIChip && (
          <Chip label={"AI"} icon={<FruitifyIcon name={"UseAI"} />} />
        )}
      </Stack>
      <Typography variant="subtitle1">{subtitle}</Typography>
    </CardContent>
    <Button
      sx={{ width: "100%" }}
      variant="outlined"
      startIcon={<Add />}
      onClick={onAdd}
      disabled={disabled}
    >
      Add
    </Button>
  </Card>
);

const UploadQuestionCard = ({
  details,
  onRefresh,
  setExtracting: setExtractingProp,
  onExtractionUpdate,
  singleQuestion = false,
  quiz = false,
  assignment = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleSetExtracting = (extracting) => {
    if (!extracting) setOpen(false);
    setExtractingProp?.(extracting);
  };

  return (
    <>
      <QuestionExtraction // TODO: we need to ensure that only LONG questions are extracted during assignment create
        open={open}
        setOpen={setOpen}
        onUpdate={onExtractionUpdate}
        subject={details?.subject}
        yearGroup={details?.yearGroup}
        setExtracting={handleSetExtracting}
        details={details}
        singleQuestion={singleQuestion}
        assignmentOrQuizId={details?.id}
        onRefresh={onRefresh}
        quizCreate={quiz}
        assignmentCreate={assignment}
      />
      <AddQuestionActionCard
        showAIChip
        title="Upload and let AI help"
        subtitle="Upload a document and let Fruitify extract existing questions and mark schemes"
        iconName="Publish"
        onAdd={() => setOpen(true)}
      />
    </>
  );
};

const CreateQuestionCard = ({
  details,
  existingQuestion,
  setExistingQuestion,
  onComplete,
  onRefresh = null,
  disabled = false,
  quiz = false,
  assignmentCreate = false,
}) => {
  // TODO: we need to ensure that only LONG questions are allowed to be created during assignment create
  const [open, setOpen] = useState(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);

  useEffect(() => {
    if (existingQuestion) {
      setOpen(true);
    }
  }, [existingQuestion]);

  const handleClose = () => {
    setOpen(false);
    setExistingQuestion(null);
  };
  return (
    <>
      <CreateQuestionDialog
        assignmentCreate={assignmentCreate}
        open={open}
        setOpen={handleClose}
        forSubject={details.subject}
        forEducationLevel={details.educationLevel}
        forYearGroup={details.yearGroup}
        createdQuestion={existingQuestion}
        setCreatedQuestion={setExistingQuestion}
        onComplete={onComplete}
        quiz={quiz}
      />
      <CreateQuizDialog
        open={quizDialogOpen}
        setOpen={setQuizDialogOpen}
        quizDetails={details}
        onRefresh={onRefresh}
      />
      {quiz ? (
        <AddQuestionActionCard
          showAIChip
          title="Quick create"
          subtitle="Choose the number of questions and let Fruitify create the quiz for you"
          iconName="Edit"
          onAdd={() => setQuizDialogOpen(true)}
          disabled={disabled}
        />
      ) : (
        <AddQuestionActionCard
          showAIChip
          title="Create your own questions"
          subtitle="Write your own questions and mark schemes, or let Fruitify create them for you"
          iconName="Edit"
          onAdd={() => setOpen(true)}
          disabled={disabled}
        />
      )}
    </>
  );
};

const ChooseQuestionCard = ({
  details,
  onQuestionAdd,
  chosenQuestions,
  quiz = false,
}) => {
  // TODO: we need to ensure that only LONG questions are allowed to be selected during assignment create
  const [open, setOpen] = useState(false);

  const handleQuestionSelected = (question) => {
    onQuestionAdd(question);
    setOpen(false);
  };
  return (
    <>
      <ChooseExistingQuestionDialog
        open={open}
        setOpen={setOpen}
        subject={details.subject}
        educationLevel={details.educationLevel}
        onQuestionSelected={handleQuestionSelected}
        chosenQuestions={chosenQuestions}
        quiz={quiz}
      />
      <AddQuestionActionCard
        title="Choose existing questions"
        subtitle="Select an existing question Fruitify’s Question Hub"
        iconName="FolderOpen"
        onAdd={() => setOpen(true)}
      />
    </>
  );
};

const SelectedQuestions = ({
  questions,
  onQuestionEditClicked,
  onCheckBoxClick,
  onRemoveClicked,
}) => {
  if (!questions || questions.length === 0) {
    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="20vh"
            textAlign="center"
            gap={2}
          >
            <HelpOutline fontSize="large" sx={{ fontSize: 60 }} />
            <Typography variant={"h3"}>No questions yet</Typography>
            <Typography variant={"subtitle1"}>
              Start by adding or uploading questions. They’ll show up here once
              created.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }
  return (
    <SortableQuestionList
      questions={questions}
      onQuestionEditClicked={onQuestionEditClicked}
      onCheckBoxClick={onCheckBoxClick}
      onRemoveClicked={onRemoveClicked}
    />
  );
};

const AddQuestions = ({
  details,
  questions,
  ongoingJobs = [],
  onRemoveQuestions,
  onAddQuestions,
  onRefresh = null,
  quiz = false,
  assignment = false,
  disabled = false,
}) => {
  const [questionToEdit, setQuestionToEdit] = useState(null);

  return (
    <Box
      sx={{
        mt: 2,
        opacity: disabled && 0.2,
        pointerEvents: disabled && "none",
      }}
    >
      <Typography variant="h3">Add questions</Typography>
      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        How would you like to add your questions?
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{
          width: "100%",
          mt: 2,
          mb: 2,
        }}
      >
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <UploadQuestionCard
            details={details}
            onRefresh={onRefresh}
            quiz={quiz}
            assignment={assignment}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <CreateQuestionCard
            details={details}
            existingQuestion={questionToEdit}
            setExistingQuestion={setQuestionToEdit}
            onComplete={(question) => onAddQuestions([question])}
            onRefresh={onRefresh}
            assignmentCreate={assignment}
            quiz={quiz}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <ChooseQuestionCard
            details={details}
            onQuestionAdd={(q) => onAddQuestions([q])}
            chosenQuestions={questions}
            quiz={quiz}
          />
        </Grid>
      </Grid>
      <Typography variant="h5">Questions</Typography>
      <Typography variant="subtitle1">
        Your added questions will appear below. Select the ones you want to use.
        Manually added questions are automatically selected.{" "}
      </Typography>
      <JobsAccordion jobs={ongoingJobs} onRefresh={onRefresh} />
      <SelectedQuestions
        questions={questions}
        onQuestionEditClicked={(e) => setQuestionToEdit(e)}
        onRemoveClicked={onRemoveQuestions} // TODO: replace with remove
      />
    </Box>
  );
};

export {
  AddQuestions,
  ChooseQuestionCard,
  CreateQuestionCard,
  UploadQuestionCard,
};
